// src/components/FiscalPanel.jsx
import React from "react";
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";
import SelectPaisesEstados from "./SelectPaisesEstados";
import NotificationsPortal, { notify } from "./NotificationsPortal";
import { X509Certificate } from "@peculiar/x509";
import { useTranslation } from "react-i18next";

export default function FiscalPanel() {
  const { t } = useTranslation();
  const { user, orgId } = useAuth();
  const [isEditing, setIsEditing] = React.useState(false);
  const [fiscal, setFiscal] = React.useState({
    tipoPersona: "Persona Moral",
    rfc: "—",
    razonSocial: "—",
    regimenFiscal: "—",
    email: "—",
    calle: "—",
    numExt: "—",
    numInt: "—",
    cp: "—",
    municipio: "—",
    estado: "—",
    pais: "México",
    csd: {
      numero: "—",
      vigencia: "—",
      cerNombre: "",
      keyNombre: "",
    },
    fiel: {
      numero: "—",
      vigencia: "—",
      cerNombre: "",
      keyNombre: "",
    },
  });

  // ====== Form ======
  const [form, setForm] = React.useState({
    tipoPersona: "",
    rfc: "",
    razonSocial: "",
    regimenFiscal: "",
    email: "",
    calle: "",
    numExt: "",
    numInt: "",
    cp: "",
    municipio: "",
    estado: "",
    pais: "México",

    // Archivos
    csdCerFile: null,
    csdKeyFile: null,
    csdPassword: "",
    fielCerFile: null,
    fielKeyFile: null,
    fielPassword: "",
  });

  const csdCerRef = React.useRef(null);
  const csdKeyRef = React.useRef(null);
  const fielCerRef = React.useRef(null);
  const fielKeyRef = React.useRef(null);

  // ====== Firestore ======
  React.useEffect(() => {
    if (!user?.uid || !orgId) return;

    const ref = doc(db, "orgs", orgId, "settings", "fiscal");
    const unsub = onSnapshot(ref, (snap) => {
      const f = snap.exists() ? snap.data() || {} : {};

      setFiscal({
        tipoPersona: f.tipoPersona ?? "Persona Moral",
        rfc: f.rfc ?? "—",
        razonSocial: f.razonSocial ?? "—",
        regimenFiscal: f.regimenFiscal ?? "—",
        email: f.email ?? "—",
        calle: f.calle ?? "—",
        numExt: f.numExt ?? "—",
        numInt: f.numInt ?? "—",
        cp: f.cp ?? "—",
        municipio: f.municipio ?? "—",
        estado: f.estado ?? "—",
        pais: f.pais ?? "México",

        csd: {
          numero: f.csd?.numero ?? "—",
          vigencia: f.csd?.vigencia ?? "—",
          cerNombre: f.csd?.cerNombre ?? "",
          keyNombre: f.csd?.keyNombre ?? "",
          password: f.csd?.password ?? "",
        },
        fiel: {
          numero: f.fiel?.numero ?? "—",
          vigencia: f.fiel?.vigencia ?? "—",
          cerNombre: f.fiel?.cerNombre ?? "",
          keyNombre: f.fiel?.keyNombre ?? "",
          password: f.fiel?.password ?? "",
        },
      });
    });

    return () => unsub();
  }, [user?.uid, orgId]);

  const startEdit = () => {
    setForm({
      tipoPersona: fiscal.tipoPersona,
      rfc: fiscal.rfc === "—" ? "" : fiscal.rfc,
      razonSocial: fiscal.razonSocial === "—" ? "" : fiscal.razonSocial,
      regimenFiscal: fiscal.regimenFiscal === "—" ? "" : fiscal.regimenFiscal,
      email: fiscal.email === "—" ? "" : fiscal.email,
      calle: fiscal.calle === "—" ? "" : fiscal.calle,
      numExt: fiscal.numExt === "—" ? "" : fiscal.numExt,
      numInt: fiscal.numInt === "" ? "" : fiscal.numInt,
      cp: fiscal.cp === "—" ? "" : fiscal.cp,
      municipio: fiscal.municipio === "—" ? "" : fiscal.municipio,
      estado: fiscal.estado === "—" ? "" : fiscal.estado,
      pais: fiscal.pais || "México",

      csdCerFile: null,
      csdKeyFile: null,
      fielCerFile: null,
      fielKeyFile: null,
      csdPassword: fiscal.csd.password || "", // <-- Cambiar de "" a esto
      fielPassword: fiscal.fiel.password || "",
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm((f) => ({
      ...f,
      csdCerFile: null,
      csdKeyFile: null,
      fielCerFile: null,
      fielKeyFile: null,
      csdPassword: "",
      fielPassword: "",
    }));
  };

  const change = async (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const file = files[0];

      // --- LÓGICA PARA LEER EL CERTIFICADO ---
      if (name === "csdCerFile" || name === "fielCerFile") {
        try {
          const buffer = await file.arrayBuffer();
          const cert = new X509Certificate(buffer);

          // Extraer número de serie (formato SAT: cada segundo byte)
          const serialHex = cert.serialNumber;
          let serialSAT = "";
          for (let i = 1; i < serialHex.length; i += 2) {
            serialSAT += serialHex[i];
          }

          // Extraer fechas
          const vencimiento = cert.notAfter.toLocaleDateString();

          // Actualizar el estado 'fiscal' temporalmente para mostrarlo en la UI
          // mientras el usuario decide si guardar o no.
          const tipo = name === "csdCerFile" ? "csd" : "fiel";

          setFiscal((prev) => ({
            ...prev,
            [tipo]: {
              ...prev[tipo],
              numero: serialSAT,
              vigencia: vencimiento,
            },
          }));

          notify(
            "success",
            t("toasts.certValid", {
              defaultValue: "Certificado válido: {{serial}}",
              serial: serialSAT,
            }),
          );
        } catch (err) {
          console.error("Error al leer certificado:", err);
          notify(
            "error",
            t(
              "toasts.certInvalid",
              "El archivo no es un certificado .cer válido",
            ),
          );
          return;
        }
      }

      setForm((f) => ({ ...f, [name]: file }));
      e.target.value = "";
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const save = async () => {
    try {
      if (!user?.uid) throw new Error(t("errors.noUser", "No hay usuario."));

      const max2mb = 2 * 1024 * 1024;
      const check2mb = (file, labelKey) => {
        if (file && file.size > max2mb) {
          notify(
            "error",
            t("toasts.fileExceeds2mb", {
              defaultValue: "{{label}} excede 2MB.",
              label: t(labelKey),
            }),
          );
          return false;
        }
        return true;
      };

      if (!check2mb(form.csdCerFile, "fiscal.files.csdCer")) return;
      if (!check2mb(form.csdKeyFile, "fiscal.files.csdKey")) return;
      if (!check2mb(form.fielCerFile, "fiscal.files.fielCer")) return;
      if (!check2mb(form.fielKeyFile, "fiscal.files.fielKey")) return;

      const storage = getStorage();

      // Subidas (solo si selecciona archivo)
      const upload = async (file, folder) => {
        if (!file) return { url: "", nombre: "" };
        const path = `orgFiscal/${orgId}/${user.uid}/${folder}/${Date.now()}-${file.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);
        return { url, nombre: file.name, updatedAt: Timestamp.now() };
      };

      const csdCerUp = await upload(form.csdCerFile, "csd");
      const csdKeyUp = await upload(form.csdKeyFile, "csd");
      const fielCerUp = await upload(form.fielCerFile, "fiel");
      const fielKeyUp = await upload(form.fielKeyFile, "fiel");

      const payload = {
        tipoPersona: (form.tipoPersona || "").trim() || "Persona Moral",
        rfc: (form.rfc || "").trim().toUpperCase(),
        razonSocial: (form.razonSocial || "").trim(),
        regimenFiscal: (form.regimenFiscal || "").trim(),
        email: (form.email || "").trim(),
        calle: (form.calle || "").trim(),
        numExt: (form.numExt || "").trim(),
        numInt: (form.numInt || "").trim(),
        cp: (form.cp || "").trim(),
        municipio: (form.municipio || "").trim(),
        estado: (form.estado || "").trim(),
        pais: (form.pais || "").trim() || "México",

        // Nota: “numero/vigencia” los puedes llenar después (o extraer del cert).
        csd: {
          numero: fiscal.csd.numero,
          vigencia: fiscal.csd.vigencia,
          cerUrl: csdCerUp.url || fiscal.csd.cerUrl || "",
          cerNombre: csdCerUp.nombre || fiscal.csd.cerNombre || "",
          keyUrl: csdKeyUp.url || fiscal.csd.keyUrl || "",
          keyNombre: csdKeyUp.nombre || fiscal.csd.keyNombre || "",
          password: form.csdPassword ? form.csdPassword : undefined,
        },
        fiel: {
          numero: fiscal.fiel.numero,
          vigencia: fiscal.fiel.vigencia,
          cerUrl: fielCerUp.url || fiscal.fiel.cerUrl || "",
          cerNombre: fielCerUp.nombre || fiscal.fiel.cerNombre || "",
          keyUrl: fielKeyUp.url || fiscal.fiel.keyUrl || "",
          keyNombre: fielKeyUp.nombre || fiscal.fiel.keyNombre || "",
          password: form.fielPassword ? form.fielPassword : undefined,
        },
      };

      // Limpia undefined para no pisar contraseñas si no escribe
      if (payload.csd.password === undefined) delete payload.csd.password;
      if (payload.fiel.password === undefined) delete payload.fiel.password;

      await setDoc(
        doc(db, "orgs", orgId, "settings", "fiscal"),
        {
          ...payload,
          updatedAt: Timestamp.now(),
          updatedBy: user.uid,
        },
        { merge: true },
      );

      notify(
        "success",
        t("toasts.fiscalSaved", "Información fiscal guardada."),
      );
      setIsEditing(false);
    } catch (err) {
      notify(
        "error",
        t("toasts.saveError", {
          defaultValue: "Error al guardar: {{msg}}",
          msg: err?.message || "—",
        }),
      );
    }
  };

  const dis = !isEditing;

  return (
    <>
      <NotificationsPortal />

      <div className="config-section fiscal-wrap">
        <div className="fiscal-card">
          <div className="fiscal-head">
            <h3 className="fiscal-title">
              {t("fiscal.ui.title", "Datos Fiscales")}
            </h3>

            {!isEditing ? (
              <button type="button" className="info-edit" onClick={startEdit}>
                <i className="bi bi-pencil" />
                <span>{t("actions.edit", "Editar")}</span>
              </button>
            ) : (
              <div className="fiscal-actions">
                <button
                  type="button"
                  className="fiscal-cancel"
                  onClick={cancelEdit}
                >
                  <i className="bi bi-x-lg" />
                  <span>{t("actions.cancel", "Cancelar")}</span>
                </button>

                <button type="button" className="info-save" onClick={save}>
                  <i className="bi bi-save" />
                  <span>{t("actions.save", "Guardar")}</span>
                </button>
              </div>
            )}
          </div>

          <div className="fiscal-divider" />

          <div className="fiscal-grid">
            <div className="fiscal-field fiscal-span-2">
              <label className="fiscal-label">
                {t("fiscal.ui.personType", "Tipo de Persona")}{" "}
                <span className="fiscal-req">*</span>
              </label>
              <select
                className="fiscal-control"
                name="tipoPersona"
                value={(isEditing ? form.tipoPersona : fiscal.tipoPersona) ?? "Persona Moral"}
                onChange={change}
                disabled={dis}
              >
                <option value="Persona Moral">Persona Moral</option>
                <option value="Persona Física">Persona Física</option>
              </select>
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("company.taxId", "RFC")}{" "}
                <span className="fiscal-req">*</span>
              </label>
              <input
                className="fiscal-control"
                name="rfc"
                value={(isEditing ? form.rfc : fiscal.rfc) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("company.businessName", "Razón Social")}{" "}
                <span className="fiscal-req">*</span>
              </label>
              <input
                className="fiscal-control"
                name="razonSocial"
                value={(isEditing ? form.razonSocial : fiscal.razonSocial) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("fiscal.ui.regime", "Régimen Fiscal")}{" "}
                <span className="fiscal-req">*</span>
              </label>
              <select
                className="fiscal-control"
                name="regimenFiscal"
                value={(isEditing ? form.regimenFiscal : fiscal.regimenFiscal) ?? ""}
                onChange={change}
                disabled={dis}
              >
                <option value="">{t("common.select", "Seleccionar")}</option>
                <option value="601 - General de Ley Personas Morales">
                  601 - General de Ley Personas Morales
                </option>
                <option value="603 - Personas Morales con Fines no Lucrativos">
                  603 - Personas Morales con Fines no Lucrativos
                </option>
                <option value="605 - Sueldos y Salarios e Ingresos Asimilados a Salarios">
                  605 - Sueldos y Salarios e Ingresos Asimilados a Salarios
                </option>
                <option value="606 - Arrendamiento">606 - Arrendamiento</option>
                <option value="607 - Régimen de Enajenacion o Adquision de Bienes">
                  607 - Régimen de Enajenación o Adquisión de Bienes
                </option>
                <option value="608 - Demás ingresos">
                  608 - Demás ingresos
                </option>
                <option value="610 - Residentes en el Extranjero sin Estableciemiento Permanente en México">
                  610 - Residentes en el Extranjero sin Estableciemiento
                  Permanente en México
                </option>
                <option value="611 - Ingresos por Dividendos (socios y accionistas)">
                  611 - Ingresos por Dividendos (socios y accionistas)
                </option>
                <option value="612 - Personas Físicas con Actividades Empresariales">
                  612 - Personas Físicas con Actividades Empresariales
                </option>
                <option value="614 - Ingresos por Intereses">
                  614 - Ingresos por Intereses
                </option>
                <option value="615 - Régimen de los Ingresos por Obtención de Premios">
                  615 - Régimen de los Ingresos por Obtención de Premios
                </option>
                <option value="616 - Sin Obligaciones Fiscales">
                  616 - Sin Obligaciones Fiscales
                </option>
                <option value="620 - Sociedades Cooperativas de Producción que Optan por diferir sus ingresos">
                  620 - Sociedades Cooperativas de Producción que Optan por
                  diferir sus ingresos
                </option>
                <option value="621 - Incorporación Fiscal">
                  621 - Incorporación Fiscal
                </option>
                <option value="622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras">
                  622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras
                </option>
                <option value="623 - Opcional para Grupos de Sociedades">
                  623 - Opcional para Grupos de Sociedades
                </option>
                <option value="624 - Coordinados">624 - Coordinados</option>
                <option value="625 - Régimen de las Actividades Empresariales con Ingresos a través de Plataformas Tecnológicas">
                  625 - Régimen de las Actividades Empresariales con Ingresos a
                  través de Plataformas Tecnológicas
                </option>
                <option value="626 - Régimen Simplicado de Confianza">
                  626 - Régimen Simplicado de Confianza
                </option>
              </select>
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("fiscal.ui.email", "Email")}
              </label>
              <input
                className="fiscal-control"
                name="email"
                value={(isEditing ? form.email : fiscal.email) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field fiscal-span-2">
              <label className="fiscal-label">
                {t("fiscal.ui.street", "Calle")}
              </label>
              <input
                className="fiscal-control"
                name="calle"
                value={(isEditing ? form.calle : fiscal.calle) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("fiscal.ui.extNumber", "Número Exterior")}
              </label>
              <input
                className="fiscal-control"
                name="numExt"
                value={(isEditing ? form.numExt : fiscal.numExt) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("fiscal.ui.intNumber", "Número Interior")}
              </label>
              <input
                className="fiscal-control"
                name="numInt"
                value={(isEditing ? form.numInt : fiscal.numInt) ?? ""}
                onChange={change}
                disabled={dis}
                placeholder={t("common.optional", "Opcional")}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("address.zip", "Código Postal")}{" "}
                <span className="fiscal-req">*</span>
              </label>
              <input
                className="fiscal-control"
                name="cp"
                value={(isEditing ? form.cp : fiscal.cp) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("fiscal.ui.city", "Municipio")}
              </label>
              <input
                className="fiscal-control"
                name="municipio"
                value={(isEditing ? form.municipio : fiscal.municipio) ?? ""}
                onChange={change}
                disabled={dis}
              />
            </div>

            <SelectPaisesEstados
              pais={isEditing ? form.pais : fiscal.pais}
              estado={isEditing ? form.estado : fiscal.estado}
              onChange={change}
              disabled={dis}
            />
          </div>
        </div>

        {/* ====== 2 CARDS: CSD y FIEL ====== */}
        <div className="fiscal-certs">
          {/* CSD */}
          <div className="cert-card">
            <h3 className="cert-title">{t("certs.csdTitle")}</h3>
            <div className="cert-divider" />

            <div className="cert-box csd">
              <div className="cert-box-row">
                <span>{t("certs.certNumber")}</span>
                <b>{fiscal.csd.numero}</b>
              </div>
              <div className="cert-box-row">
                <span>{t("certs.validity")}</span>
                <b>{fiscal.csd.vigencia}</b>
              </div>
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("files.cerLabel")}</span>
              <button
                type="button"
                className={`cert-file-btn ${
                  form.csdCerFile ? "file-selected" : ""
                }`}
                onClick={() => isEditing && csdCerRef.current?.click()}
                disabled={!isEditing}
              >
                {/* Cambiamos el icono si ya hay un archivo seleccionado */}
                <i
                  className={
                    form.csdCerFile
                      ? "bi bi-file-earmark-check"
                      : "bi bi-upload"
                  }
                />
                <span>
                  {form.csdCerFile
                    ? form.csdCerFile.name
                    : (fiscal.csd.cerNombre ?? "") || t("files.selectCer")}
                </span>
              </button>
              <input
                ref={csdCerRef}
                type="file"
                accept=".cer"
                name="csdCerFile"
                onChange={change}
                style={{ display: "none" }}
              />
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("files.keyLabel")}</span>
              <button
                type="button"
                /* Añadimos una clase dinámica 'file-selected' si ya hay un archivo en el form */
                className={`cert-file-btn ${
                  form.csdKeyFile ? "file-selected" : ""
                }`}
                onClick={() => isEditing && csdKeyRef.current?.click()}
                disabled={!isEditing}
                title={form.csdKeyFile?.name || fiscal.csd.keyNombre || ""}
              >
                {/* El icono cambia a un círculo con check si el archivo ya está cargado */}
                <i
                  className={
                    form.csdKeyFile ? "bi bi-check-circle-fill" : "bi bi-upload"
                  }
                />

                <span>
                  {form.csdKeyFile
                    ? form.csdKeyFile.name
                    : (fiscal.csd.keyNombre ?? "") || t("files.selectKey")}
                </span>
              </button>
              <input
                ref={csdKeyRef}
                type="file"
                accept=".key"
                name="csdKeyFile"
                onChange={change}
                style={{ display: "none" }}
              />
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("passwords.fiel")}</span>
              <input
                className="cert-pass"
                type="password"
                name="csdPassword"
                value={(isEditing ? form.csdPassword : fiscal.csd.password) ?? ""}
                onChange={change}
                disabled={dis}
                placeholder={t(
                  "fiscal.passwords.placeholder",
                  "Ingrese la contraseña",
                )}
              />
            </div>

            {/* Reemplaza el bloque final de CSD */}
            {!isEditing ? (
              <button type="button" className="cert-edit" onClick={startEdit}>
                <i className="bi bi-pencil" />
                <span>{t("actions.edit", "Editar")}</span>
              </button>
            ) : (
              <div className="fiscal-actions" style={{ marginTop: "20px" }}>
                <button
                  type="button"
                  className="fiscal-cancel"
                  onClick={cancelEdit}
                >
                  <i className="bi bi-x-lg" />
                  <span>{t("actions.cancel", "Cancelar")}</span>
                </button>
                <button type="button" className="info-save" onClick={save}>
                  <i className="bi bi-save" />
                  <span>{t("actions.save", "Guardar")}</span>
                </button>
              </div>
            )}
          </div>

          {/* FIEL */}
          <div className="cert-card">
            <h3 className="cert-title">{t("certs.fielTitle")}</h3>
            <div className="cert-divider" />

            <div className="cert-box fiel">
              <div className="cert-box-row">
                <span>{t("certs.certNumber")}</span>
                <b>{fiscal.fiel.numero}</b>
              </div>
              <div className="cert-box-row">
                <span>{t("certs.validity")}</span>
                <b>{fiscal.fiel.vigencia}</b>
              </div>
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("files.cerLabel")}</span>
              <button
                type="button"
                className={`cert-file-btn ${form.fielCerFile ? "file-selected" : ""}`}
                onClick={() => isEditing && fielCerRef.current?.click()}
                disabled={!isEditing}
                title={form.fielCerFile?.name || fiscal.fiel.cerNombre || ""}
              >
                <i
                  className={
                    form.fielCerFile
                      ? "bi bi-check-circle-fill"
                      : "bi bi-upload"
                  }
                />
                <span>
                  {form.fielCerFile
                    ? form.fielCerFile.name
                    : fiscal.fiel.cerNombre || t("files.selectCer")}
                </span>
              </button>

              <input
                ref={fielCerRef}
                type="file"
                accept=".cer"
                name="fielCerFile"
                onChange={change}
                style={{ display: "none" }}
              />
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("files.keyLabel")}</span>
              <button
                type="button"
                className={`cert-file-btn ${form.fielKeyFile ? "file-selected" : ""}`}
                onClick={() => isEditing && fielKeyRef.current?.click()}
                disabled={!isEditing}
                title={form.fielKeyFile?.name || fiscal.fiel.keyNombre || ""}
              >
                <i
                  className={
                    form.fielKeyFile
                      ? "bi bi-check-circle-fill"
                      : "bi bi-upload"
                  }
                />
                <span>
                  {form.fielKeyFile
                    ? form.fielKeyFile.name
                    : fiscal.fiel.keyNombre || t("files.selectKey")}
                </span>
              </button>

              <input
                ref={fielKeyRef}
                type="file"
                accept=".key"
                name="fielKeyFile"
                onChange={change}
                style={{ display: "none" }}
              />
            </div>

            <div className="cert-field">
              <span className="fiscal-label">{t("passwords.fiel")}</span>
              <input
                className="cert-pass"
                type="password"
                name="fielPassword"
                value={(isEditing ? form.fielPassword : fiscal.fiel.password) ?? ""}
                onChange={change}
                disabled={dis}
                placeholder={t("passwords.placeholder")}
              />
            </div>

            {/* Bloque final (editar / guardar) */}
            {!isEditing ? (
              <button type="button" className="cert-edit" onClick={startEdit}>
                <i className="bi bi-pencil" />
                <span>{t("actions.edit")}</span>
              </button>
            ) : (
              <div className="fiscal-actions" style={{ marginTop: "20px" }}>
                <button
                  type="button"
                  className="fiscal-cancel"
                  onClick={cancelEdit}
                >
                  <i className="bi bi-x-lg" />
                  <span>{t("actions.cancel")}</span>
                </button>

                <button type="button" className="info-save" onClick={save}>
                  <i className="bi bi-save" />
                  <span>{t("actions.save")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
